import {Injectable, Optional} from '@angular/core';
import {AuthHttpService} from './auth-http.service';
import {LoginRequestModel} from '../models/login-request-model';
import {Router} from '@angular/router';
import {LoginResponseModelNew, PermissionGroupPartModel} from '../models/login-response-model-new';
import {UserDetailsModel} from '../models/user-details-model';
import {LogoutTypeEnum} from '../enums/logout-type';
import {JwtHelper} from '../../../core/services/jwt.helper';
import {AppAuthenticationAdapterService} from '../../../core/services/app-authentication-adapter.service';
import {ErrorTypeEnum} from '../../../shared/enums/error-type-enum';
import {AppConstants} from '../../../core/app-constants';
import {getEnumValue} from '../../projects/utils/enum.utils';
import {SecFEPermissionPartnerAppEnum} from '../../../shared/enums/sec-f-e-permission-partner-app-enum';
import {AppEventsService} from '../../../core/services/app-events.service';
import {UserContextService} from '../../../core/services/user-context.service';
import {LocalStorageService} from '../../../core/services/local-storage.service';
import {DataProcessingAgreementService} from '../../data-processing-agreement/services/data-processing-agreement.service';
import {WhoAmIHttpService} from '../../../core/services/who-am-i-http.service';
import {ToastService} from '../../../shared/services/toast.service';

@Injectable({providedIn: 'root'})
export class AuthenticationService {

    public readonly LOGIN_URL: string = '/auth/login';  // used in the token interceptor
    public readonly HOME_PAGE_URL: string = '/';  // used in the token interceptor

    lastCheckedAccessToken: string;

    constructor(
        private authHttpService: AuthHttpService,
        private localStorageService: LocalStorageService,
        private userContextService: UserContextService,
        private router: Router,
        private appEventsService: AppEventsService,
        private whoAmIHttpService: WhoAmIHttpService,
        private appAuthenticationAdapterService: AppAuthenticationAdapterService,
        private dataProcessingAgreementService: DataProcessingAgreementService,
        private toastService: ToastService
    ) {
        this.appEventsService.appEvent$.subscribe(async (event: {eventName: string, data: any}) => {
            if (event.eventName === this.appEventsService.APP_EVENT_ENUM.LOGOUT) {
                await this.logout();
            }
        });
    }

    public static isAuthenticatedForCurrentAppFn(permissionGroups: PermissionGroupPartModel[]) {

        // console.log('AppConstants', AppConstants);

        // console.log('AppConstants.APP_REQUIRED_PERMISSION_ARR', AppConstants.APP_REQUIRED_PERMISSION_ARR);


        let appRequiredPermissionArr = AppConstants.APP_REQUIRED_PERMISSION_ARR.map(item => getEnumValue(SecFEPermissionPartnerAppEnum, item));
        if (!appRequiredPermissionArr) {
            console.warn('No required permissions defined in AppConstants.APP_REQUIRED_PERMISSION_ARR');
            return true;
        }

        // find any group that contains any of the required permissions
        return permissionGroups.some(group =>
            group.permissions.some(permission => appRequiredPermissionArr.includes(permission))
        );
    }

    async loginNew(credentials: LoginRequestModel): Promise<LoginResponseModelNew> {
        return this.authHttpService.loginNew(credentials);
    }

    async processDataFromLoginResponse(loginResponse: LoginResponseModelNew) {

        if (!AuthenticationService.isAuthenticatedForCurrentAppFn(loginResponse.permissionGroups)) {
            await this.router.navigate([AppConstants.LOGIN_URL]);
            throw new Error(ErrorTypeEnum.ERROR_NO_REQUIRED_PERMISSIONS);

        }

        this.localStorageService.saveAuthTokens(loginResponse.accessToken, loginResponse.refreshToken);

        try {

            // whoAmIResponse contains all user data and more than login response, so processWhoAmIResponse is used the save all user data
            let whoAmIResponse = await this.whoAmIHttpService.whoAmI();
            await this.userContextService.processWhoAmIResponse(whoAmIResponse);

        } catch (e) {
            console.error('Error fetching user details via whoAmI:', e);
            this.toastService.showError("Der Server ist momentan nicht erreichbar. Bitte versuchen Sie es später erneut.");
            await this.logout();
        }
        await this.router.navigate([this.HOME_PAGE_URL]); // navigate to dashboard or home page
        await this.dataProcessingAgreementService.processDataProcessingAgreement();
        // await this.servicesApprovalService.showWarningForPrimaryOrRepresentativeIfNeeded();
    }


    async logout(logoutType: LogoutTypeEnum = LogoutTypeEnum.CURRENT_DEVICE) {

        console.log('AuthenticationService logout');


        let accessToken = this.getAccessToken();
        try {
            if (accessToken && !JwtHelper.isTokenExpired(accessToken)) {
                await this.authHttpService.logout(logoutType);
            }
        } catch (e) {
            console.error('Logout failed', e);
        }
        this.appAuthenticationAdapterService.finishOldLogout();
        await this.removeUserDetails();
        await this.router.navigate([AppConstants.LOGIN_URL]);
    }

    async removeUserDetails() {
        this.localStorageService.removeAuthTokens();
        this.userContextService.removeUserDetails();
    }

    async isAuthenticated(): Promise<boolean> {
        let debug = false;
        // console.warn('isAuthenticated');
        const lastAuthErrorType = this.localStorageService.getAuthErrorType();
        if (lastAuthErrorType) {
            return false;
        }

        let accessToken = this.localStorageService.getAccessToken();

        if (!accessToken) {
            return false;
        }

        if (accessToken !== this.lastCheckedAccessToken || JwtHelper.isTokenExpired(accessToken)) {
            this.lastCheckedAccessToken = await this.ensureAccessTokenIsFresh();

            if (debug) {
                let refreshToken = this.localStorageService.getRefreshToken();
                console.log('isAuthenticated accessToken changed', accessToken, JwtHelper.getTokenExpirationDate(accessToken), 'will expire in', (JwtHelper.getTokenExpirationDate(accessToken).getTime() - Date.now()) / 1000 / 60, 'minutes');
                console.log('isAuthenticated refreshToken', refreshToken, JwtHelper.getTokenExpirationDate(refreshToken), 'will expire in', (JwtHelper.getTokenExpirationDate(refreshToken).getTime() - Date.now()) / 1000 / 60, 'minutes');

            }
        }
        return true;
    }


    getAccessToken(): string | null {
        return this.localStorageService.getAccessToken();
    }

    getRefreshToken(): string | null {
        return this.localStorageService.getRefreshToken();
    }

    async ensureAccessTokenIsFresh(): Promise<string> {
        console.warn('ensureAccessTokenIsFresh');

        let accessToken = this.getAccessToken();

        if (!accessToken) {
            console.log('Access token is null. Logging out the user.');
            await this.logout(LogoutTypeEnum.CURRENT_DEVICE);
            return '';
        }

        if (JwtHelper.isTokenExpired(accessToken)) {
            try {
                console.log('Access token is expired, refreshing via refresh token');
                const refreshToken = this.getRefreshToken();
                if (!refreshToken) {
                    await this.logout(LogoutTypeEnum.CURRENT_DEVICE);
                    return '';
                }
                const response = await this.authHttpService.refreshTokenNew(refreshToken);
                // Save the new tokens in local storage
                this.localStorageService.saveAuthTokens(response.accessToken, response.refreshToken);
                accessToken = response.accessToken;
            } catch (error) {
                await this.logout(LogoutTypeEnum.CURRENT_DEVICE);
            }
        }
        return accessToken;
    }

    async verify2FA(verificationHash: string, code: string): Promise<LoginResponseModelNew> {
        return this.authHttpService.verify2FA(verificationHash, code);
    }

    async resend2FACode(verificationHash: string): Promise<{ verificationHash: string }> {
        return this.authHttpService.resend2FACode(verificationHash);
    }

    async verifyDeviceSessionStatus(): Promise<LoginResponseModelNew> {
        console.log('verifyDeviceSessionStatus');

        return await this.authHttpService.verifyDeviceSessionStatus();
    }

    async resetPassword(email: string): Promise<void> {
        return await this.authHttpService.resetPassword(email);
    }

    async updatePassword(oldPassword: string, newPassword: string): Promise<void> {
        return await this.authHttpService.updatePassword(this.userContextService.getUserDetails()?.externalId, oldPassword, newPassword);
    }


}
